import { useCallback, useSyncExternalStore } from "react";
import { type Route, defaultRoute, parseRoute, routeKey, routeToPath, sameRoute } from "./route.js";
import { recallRoute, rememberRoute } from "./session.js";

/**
 * Роутер на History API. Десятки рядків замість бібліотеки, яка вирішує
 * чужі задачі — [ADR-0013].
 *
 * Три речі, яких у «просто pushState» не буває, і саме вони й потрібні:
 * повернення на попередній горизонт, відновлення прокрутки й чесна адреса
 * замість невідомої.
 *
 * Стан маршруту живе в модулі, а не в `useState` всередині хука.
 * Це не оптимізація, а виправлення помилки: з `useState` кожен виклик
 * `useRouter()` заводив власну копію маршруту. Перехід із вкладеного екрана
 * міняв адресу, але кореневий компонент про це не дізнавався — і на екрані
 * лишався попередній масштаб. Адреса за природою одна на вкладку, тож і
 * стан має бути один.
 */
export interface Router {
  readonly route: Route;
  /** Перехід із записом в історію. */
  go(to: Route): void;
  /** Заміна поточного запису — для зуму, який не має засмічувати історію. */
  replace(to: Route): void;
  back(): void;
}

const scrolls = new Map<string, number>();

function currentPath(): string {
  return globalThis.location?.pathname ?? "/";
}

/** Початковий маршрут: адреса → памʼять навігації → сьогодні. */
function initialRoute(): { route: Route; rewrite: boolean } {
  const fromUrl = parseRoute(currentPath());
  if (fromUrl) return { route: fromUrl, rewrite: false };

  /*
   * З памʼяті відновлюється **лише час** — день, тиждень, місяць, рік.
   *
   * O5 обіцяє «продовження тієї самої роботи», а робота в Бересті
   * вимірюється часом ([ADR-0013]: адреса і є горизонтом). Налаштування,
   * обліковий запис, пошук і чужа картка роботою не є, і повертатися в них
   * з голої адреси — не послуга.
   *
   * Ціна була видима: голий домен, набраний через хвилину після того, як
   * людина дивилася «Обліковий запис», відкривав налаштування — найменш
   * показовий екран застосунку. А набирають голу адресу зазвичай у двох
   * випадках: показати застосунок комусь або почати день.
   *
   * Сам запис памʼяті не звужується: він потрібен історії й прокрутці.
   * Звужується саме **відновлення**, бо рішення тут і приймається.
   */
  const remembered = recallRoute();
  const fromMemory = remembered ? parseRoute(remembered) : null;
  if (fromMemory?.kind === "horizon") return { route: fromMemory, rewrite: true };

  // Невідома адреса не лишається в рядку: інакше оновлення сторінки
  // знову приведе в нікуди, а посилання виглядатиме робочим.
  return { route: defaultRoute(), rewrite: true };
}

// ── сховище маршруту ────────────────────────────────────────────────
// Одне на вкладку, як і сама адреса.

let current: Route | null = null;
let pendingScroll: string | null = null;
const listeners = new Set<() => void>();

/**
 * Підняття сховища при першому зверненні, а не під час імпорту модуля.
 *
 * Робота під час імпорту виглядає нешкідливо, але читає `location` тоді,
 * коли модуль випадково опинився в графі — а це може бути раніше, ніж
 * сторінка існує. Тест теж не має змоги підготувати адресу до цього моменту.
 */
function ensureInit(): Route {
  if (current !== null) return current;
  const start = initialRoute();
  if (start.rewrite) globalThis.history?.replaceState(null, "", routeToPath(start.route));
  current = start.route;
  rememberRoute(routeToPath(current));
  globalThis.addEventListener?.("popstate", onPop);
  return current;
}

function emit(): void {
  for (const listener of listeners) listener();
}

function setRoute(next: Route): void {
  // Посилання має лишатися тим самим, поки маршрут не змінився:
  // `useSyncExternalStore` порівнює знімки саме за посиланням.
  if (current !== null && sameRoute(next, current)) return;
  current = next;
  rememberRoute(routeToPath(next));
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function onPop(): void {
  const next = parseRoute(currentPath()) ?? defaultRoute();
  // Прокрутку відновлюємо після того, як екран перемалювався.
  pendingScroll = routeKey(next);
  setRoute(next);
  const key = pendingScroll;
  pendingScroll = null;
  if (key !== null) globalThis.scrollTo?.(0, scrolls.get(key) ?? 0);
}

function navigate(to: Route, mode: "push" | "replace"): void {
  const from = ensureInit();
  if (sameRoute(to, from)) return;
  // Куди повертатися очима, а не лише адресою.
  scrolls.set(routeKey(from), globalThis.scrollY ?? 0);
  const path = routeToPath(to);
  if (mode === "push") globalThis.history?.pushState(null, "", path);
  else globalThis.history?.replaceState(null, "", path);
  setRoute(to);
  globalThis.scrollTo?.(0, 0);
}

/** Скидання сховища. Лише для тестів: у застосунку вкладка одна й назавжди. */
export function __resetRouterForTests(): void {
  current = null;
  pendingScroll = null;
  scrolls.clear();
  listeners.clear();
  globalThis.removeEventListener?.("popstate", onPop);
  // Навмисно без `emit()`: підписники, що лишилися від попередньої перевірки,
  // перечитали б знімок і підняли сховище з тодішньої адреси — тобто скидання
  // саме ж і поламало б те, що скидає.
}

// ── хук ─────────────────────────────────────────────────────────────

export function useRouter(): Router {
  const route = useSyncExternalStore(subscribe, ensureInit, ensureInit);

  const go = useCallback((to: Route) => navigate(to, "push"), []);
  const replace = useCallback((to: Route) => navigate(to, "replace"), []);
  const back = useCallback(() => globalThis.history?.back(), []);

  return { route, go, replace, back };
}
